import React, { useState, useEffect } from "react";
import ReactModal from "react-modal";
import { postApi } from "../Services/apiService";
import { toast } from "react-toastify";
import "../popups.css";
import { getFamilyTreeIdFromStorage } from "../Utils/headOfLineRole";

if (process.env.NODE_ENV !== "test") {
  ReactModal.setAppElement("#root");
}

const ReorderMultipleBirthModal = ({
  isOpen = false,
  onClose,
  members = [], // Array of members in the multiple birth set
  birthType = "", // 'twins', 'triplets', 'quads', etc.
  setNumber = 0, // The set number (1, 2, 3, etc.)
}) => {
  const [orderedMembers, setOrderedMembers] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initialize ordered members when modal opens or members change
  useEffect(() => {
    if (isOpen && members.length > 0) {
      // Sort members by their current multiple_birth_order_number or by their position
      const sorted = [...members].sort((a, b) => {
        const orderA = a.multiple_birth_order_number 
          ? (typeof a.multiple_birth_order_number === 'string' 
              ? parseInt(a.multiple_birth_order_number, 10) 
              : a.multiple_birth_order_number)
          : 999;
        const orderB = b.multiple_birth_order_number 
          ? (typeof b.multiple_birth_order_number === 'string' 
              ? parseInt(b.multiple_birth_order_number, 10) 
              : b.multiple_birth_order_number)
          : 999;
        return orderA - orderB;
      });
      setOrderedMembers(sorted);
    }
  }, [isOpen, members]);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const draggedOverIndex = index;
    if (draggedIndex === draggedOverIndex) return;

    const newOrderedMembers = [...orderedMembers];
    const draggedItem = newOrderedMembers[draggedIndex];

    // Remove the dragged item
    newOrderedMembers.splice(draggedIndex, 1);
    // Insert it at the new position
    newOrderedMembers.splice(draggedOverIndex, 0, draggedItem);

    setOrderedMembers(newOrderedMembers);
    setDraggedIndex(draggedOverIndex);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const family_tree_id = getFamilyTreeIdFromStorage();

      if (!family_tree_id) {
        toast.error("Family tree ID not found");
        setLoading(false);
        return;
      }

      // Create the array in the required format
      const orderData = orderedMembers.map((member, index) => ({
        order: index + 1,
        memberId: member.id, // Use member.id with camelCase key
      }));

      // Make API call to save the order - send with "reorder" and "family_tree_id" keys
      const response = await postApi("/update-multiple-birth-order", {
        reorder: orderData,
        family_tree_id: family_tree_id
      });

      if (response?.statusCode === 200 || response?.success) {
        toast.success("Birth order updated successfully");
        onClose(true); // Pass true to indicate success
      } else {
        toast.error("Failed to update birth order");
        onClose(false);
      }
    } catch (error) {
      console.error("Error updating birth order:", error);
      toast.error("Failed to update birth order");
      onClose(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original order
    const sorted = [...members].sort((a, b) => {
      const orderA = a.multiple_birth_order_number 
        ? (typeof a.multiple_birth_order_number === 'string' 
            ? parseInt(a.multiple_birth_order_number, 10) 
            : a.multiple_birth_order_number)
        : 999;
      const orderB = b.multiple_birth_order_number 
        ? (typeof b.multiple_birth_order_number === 'string' 
            ? parseInt(b.multiple_birth_order_number, 10) 
            : b.multiple_birth_order_number)
        : 999;
      return orderA - orderB;
    });
    setOrderedMembers(sorted);
    onClose(false);
  };

  const getBirthTypeLabel = () => {
    const labels = {
      twins: "TWIN",
      triplets: "TRIPLET",
      quads: "QUAD",
      quins: "QUIN",
      sexs: "SEX",
      seps: "SEP",
      ocs: "OC",
      nuns: "NUN",
    };
    return labels[birthType] || "MULTIPLE BIRTH";
  };

  const getMemberName = (member) => {
    const firstName = member.first_name || "";
    const middleName = member.middle_name || "";
    const lastName = member.last_name || "";
    const suffix = member.suffix || "";
    
    let name = firstName;
    if (middleName) name += ` ${middleName}`;
    if (lastName) name += ` ${lastName}`;
    if (suffix) name += ` ${suffix}`;
    
    return name.trim() || member.full_name || "Unknown";
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleCancel}
      className="popup-card addmember-card"
      overlayClassName="popup-overlay addmember-overlay"
      closeTimeoutMS={200}
      ariaHideApp={false}
    >
      <div className="reorder-multiple-birth-modal">
        <div className="modal-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '20px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
            Reorder {getBirthTypeLabel()} {setNumber}
          </h2>
          <button
            onClick={handleCancel}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-content" style={{ padding: '20px' }}>
          <div style={{ marginBottom: '20px', color: '#666' }}>
            Drag and drop to reorder members. Click Save to apply changes.
          </div>

          <div className="members-list">
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '80px 1fr',
              gap: '10px',
              marginBottom: '10px',
              padding: '10px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              fontWeight: 'bold'
            }}>
              <div>Order</div>
              <div>Name</div>
            </div>

            {orderedMembers.map((member, index) => (
              <div
                key={member.id || member.user_id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr',
                  gap: '10px',
                  padding: '15px',
                  marginBottom: '8px',
                  backgroundColor: draggedIndex === index ? '#e3f2fd' : '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  cursor: 'move',
                  transition: 'background-color 0.2s',
                  boxShadow: draggedIndex === index ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                <div style={{ 
                  fontWeight: 'bold',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {index + 1}
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {getMemberName(member)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer" style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '10px',
          padding: '20px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <button
            onClick={handleCancel}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              color: '#333'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: loading ? '#ccc' : '#6366f1',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              color: '#fff'
            }}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </ReactModal>
  );
};

export default ReorderMultipleBirthModal;
